import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Database,
  TrendingUp,
  ArrowRight,
  Award,
  Settings,
  Shield,
  Send,
  Star,
  Workflow,
  Zap,
  Globe,
} from 'lucide-react';
import useSEO from '../../hooks/useSEO';

export default function DataIntelligence() {
  useSEO({
    title: 'Data & Business Intelligence | Lockie Visuals',
    description: 'Business intelligence dashboards, data pipelines and predictive analytics that turn your data into growth decisions.',
    path: '/data-intelligence'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  const services = [
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Business Intelligence",
      description: "End-to-end BI solutions with interactive dashboards, KPIs, and real-time reporting for data-driven decisions.",
      features: ["Custom Dashboards", "KPI Tracking", "Automated Reports", "Real-time Analytics"],
      price: "Starting from MK4,000,000"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "BI & Analytics",
      description: "End-to-end business intelligence solutions with interactive dashboards and actionable insights.",
      features: ["Data Visualization", "KPI Tracking", "Predictive Analytics", "Custom Dashboards"],
      price: "Starting from MK6,000,000"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Data Warehousing",
      description: "Centralized data warehouses designed for high-performance analytics and reporting.",
      features: ["ETL Pipelines", "Data Modeling", "Cloud Storage", "Query Optimization"],
      price: "Starting from MK8,000,000"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Analytics",
      description: "Real-time data processing and analytics solutions for immediate insights.",
      features: ["Stream Processing", "Live Dashboards", "Event-Driven Architecture", "Low Latency"],
      price: "Starting from MK2,500,000"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Data Governance",
      description: "Data governance frameworks ensuring data quality, compliance, and security.",
      features: ["Data Quality", "Compliance Management", "Access Control", "Audit Trails"],
      price: "Starting from MK2,000,000"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Data Maintenance",
      description: "Ongoing data management and support services to keep your data infrastructure running smoothly.",
      features: ["Data Cleaning", "Pipeline Monitoring", "Performance Tuning", "24/7 Support"],
      price: "Starting from MK3,000,000"
    }
  ];

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setNotification({
      type: 'success',
      title: 'Success!',
      message: "We've received your inquiry and will contact you within 24 hours."
    });
    setFormData({ name: '', email: '', service: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#021E23] transition-colors duration-300">
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-24 right-4 p-5 rounded-2xl shadow-2xl max-w-md z-50 border backdrop-blur-xl ${notification.type === 'success'
              ? 'bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400'
              : 'bg-red-500/10 border-red-500/20 text-red-700 dark:text-red-400'
              }`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-1.5 rounded-full ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                <Star size={14} className="text-white" />
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-sm leading-none">{notification.title}</h3>
                <p className="text-xs opacity-80 leading-relaxed font-medium">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
        <div className="relative z-10 text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-sm font-bold tracking-wider uppercase mb-4"
          >
            <BarChart3 size={16} />
            <span>Data & Analytics</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight text-gray-900 dark:text-white leading-[1.1]"
          >
            Data & Business <br />
            <span className="text-orange-500">Intelligence.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed font-medium"
          >
            Turn raw data into strategic advantage. We build the infrastructure, dashboards, and models
            that empower your organization to make smarter, faster decisions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 pt-4"
          >
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Zap className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">Real-time Insights</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Shield className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">Data Governance</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm">
              <Workflow className="w-5 h-5 text-orange-500" />
              <span className="text-[15px] font-bold text-gray-700 dark:text-gray-300 tracking-tight">ML-powered</span>
            </div>
          </motion.div>
        </div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 blur-[120px] rounded-full -z-0"></div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 border-t border-gray-100 dark:border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group p-8 rounded-3xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/10 hover:border-orange-500/30 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/5 relative overflow-hidden"
            >
              <div className="p-4 bg-orange-500/10 rounded-2xl w-fit mb-6 group-hover:bg-orange-500 transition-colors duration-300 group-hover:text-white text-orange-500">
                {service.icon}
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                {service.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed font-medium">
                {service.description}
              </p>

              <ul className="space-y-3 mb-8">
                {service.features.map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 font-bold">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-between pt-6 border-t border-gray-100 dark:border-white/10">
                <span className="text-orange-500 font-bold tracking-tight">{service.price}</span>
                <button className="p-2 rounded-full bg-gray-50 dark:bg-white/5 text-gray-400 group-hover:bg-orange-500 group-hover:text-white transition-all transform group-hover:translate-x-1">
                  <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-4 py-24 bg-[#0B1221] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-orange-300 to-orange-500 opacity-20"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <Database size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">10+</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Projects Delivered</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <BarChart3 size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">500+</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Dashboards Deployed</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <TrendingUp size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">3x</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Avg. Decision Speed</div>
              </div>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto border border-white/10 text-orange-500">
                <Award size={32} />
              </div>
              <div className="space-y-1">
                <div className="text-4xl font-bold text-white tracking-tighter">99%</div>
                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">Data Accuracy</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 whitespace-normal">
        <div className="relative rounded-2xl bg-[#0B1221] p-8 md:p-16 overflow-hidden border border-white/10">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-orange-500/10 blur-[100px] -mr-40 -mt-40"></div>

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center text-left">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                Unlock Your Data <br />
                <span className="text-orange-500">Potential.</span>
              </h2>
              <p className="text-xl text-gray-400 leading-relaxed max-w-md font-medium">
                From raw data to actionable intelligence — we help you build a data-driven organization.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <BarChart3 size={20} className="text-orange-500 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">Call Us</p>
                    <p className="font-bold text-white">0990155300 / 0888777332</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Send size={20} className="text-orange-500 group-hover:text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-bold uppercase tracking-wider">Email Us</p>
                    <p className="font-bold text-white">infoatlockievisuals@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-orange-500/50 transition-colors font-bold"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-orange-500/50 transition-colors font-bold"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Service</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleFormChange}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-orange-500/50 transition-colors appearance-none font-bold"
                  >
                    <option value="" className="bg-[#0B1221]">Select service</option>
                    {services.map((s, i) => (
                      <option key={i} value={s.title} className="bg-[#0B1221]">{s.title}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 ml-1 uppercase tracking-widest">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white outline-none focus:border-orange-500/50 transition-colors resize-none font-bold"
                    placeholder="Tell us about your data project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Start Data Project'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
